/** @format */

import { useEffect, useRef, useState } from "react";
import { FaLock } from "react-icons/fa";
import { GrMail } from "react-icons/gr";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { Link } from "react-router-dom";
import i18n from "../../utils/i18n";
import UserAvatar from "../../utils/UserAvatar";
import { email, socialLinks } from "../../utils/utils";

/* ─── Language Selector ─────────────────────────────────────────── */
const LanguageSelector = ({ changeLanguage }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const langs = [
    {
      code: "en",
      label: "English",
      flag: "/images/Flag_of_the_United_Kingdom.png",
    },
    { code: "fr", label: "Français", flag: "/images/frflag.png" },
  ];

  const current = langs.find((l) => l.code === i18n.language) ?? langs[0];

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className='relative'>
      <button
        onClick={() => setOpen((v) => !v)}
        className='flex items-center gap-1.5 px-2 py-1 rounded-lg
                   hover:bg-white/10 transition-all duration-200 text-slate-200 text-sm'
        aria-label='Select language'
      >
        <img
          src={current.flag}
          alt={current.code}
          className='w-5 h-auto rounded-sm shadow-sm'
        />
        <MdOutlineKeyboardArrowDown
          className={`transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"}`}
        />
      </button>

      {/* Dropdown */}
      <div
        className={`
          absolute top-full right-0 mt-2 w-[148px] z-50
          bg-slate-800/95 backdrop-blur-md border border-white/10
          rounded-xl shadow-2xl shadow-black/40 overflow-hidden
          transition-all duration-200 origin-top-right
          ${open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"}
        `}
      >
        {langs.map(({ code, label, flag }) => (
          <button
            key={code}
            onClick={() => {
              changeLanguage(code);
              setOpen(false);
            }}
            className={`
              w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left
              transition-colors duration-150
              ${
                i18n.language === code
                  ? "bg-orange-500/20 text-orange-300 font-medium"
                  : "text-slate-200 hover:bg-white/10"
              }
            `}
          >
            <img
              src={flag}
              className='w-5 h-auto rounded-sm shadow-sm'
              alt={code}
            />
            {label}
            {i18n.language === code && (
              <span className='ml-auto w-1.5 h-1.5 rounded-full bg-orange-400' />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

/* ─── Separator ─────────────────────────────────────────────────── */
const Sep = () => <span className='w-px h-8 bg-white rounded-full shrink-0' />;

/* ─── HeaderTop ─────────────────────────────────────────────────── */
const HeaderTop = ({
  t,
  customerInfo,
  handleLogout,
  handleSwitchToSeller,
  changeLanguage,
}) => {
  return (
    <div
      className='md-lg:hidden w-full
                 bg-slate-200
                 border-b border-white/[0.06]'
    >
      <div className='w-[95%] lg:w-[90%] mx-auto'>
        <div className='flex items-center justify-between h-[50px]'>
          {/* ── Left: email + tagline ── */}
          <div className='flex items-center gap-5 text-[13px] text-slate-400'>
            <a
              href={`mailto:${email}`}
              className='flex items-center gap-1.5 hover:text-orange-400
                         transition-colors duration-200 group'
            >
              <GrMail className='text-orange-400/80 group-hover:text-orange-400 transition-colors' />
              <span className='tracking-wide'>{email}</span>
            </a>

            <Sep />

            <span className='text-slate-500 tracking-widest text-[11px] uppercase font-medium hidden lg:inline'>
              MultiVendor Web App
            </span>
          </div>

          {/* ── Right: actions ── */}
          <div className='flex items-center gap-3'>
            {/* Social Links */}
            <div className='flex items-center gap-2 mr-1'>
              {socialLinks.map(({ icon, label, href }, i) => (
                <a
                  key={i}
                  href={href}
                  aria-label={label}
                  className='w-8 h-8 flex items-center justify-center rounded-lg
                             text-slate-400 hover:text-white hover:bg-white/10
                             transition-all duration-200 text-md'
                >
                  {icon}
                </a>
              ))}
            </div>

            <Sep />

            {/* Sell CTA */}
            <button
              onClick={handleSwitchToSeller}
              className='relative text-sm font-semibold tracking-wide uppercase
                         px-3.5 py-1.5 rounded-lg overflow-hidden group
                         bg-blue-500 text-white
                         hover:bg-blue-400
                         transition-all duration-200 shadow-md shadow-orange-500/20'
            >
              {t("Sell on GOBYMALL")}
            </button>

            <Sep />

            {/* Language */}
            <LanguageSelector changeLanguage={changeLanguage} />

            <Sep />

            {/* Auth */}
            {customerInfo ? (
              <UserAvatar
                customerInfo={customerInfo}
                handleLogout={handleLogout}
                handleSwitchToSeller={handleSwitchToSeller}
              />
            ) : (
              <Link
                to='/login'
                className='flex items-center gap-1.5 text-[13px] text-slate-300
                           hover:text-orange-400 transition-colors duration-200 group'
              >
                <FaLock className='text-[11px] group-hover:scale-110 transition-transform duration-200' />
                <span className='font-medium'>{t("Login")}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderTop;
